import { Dialog as D } from 'radix-ui'
import { lazy, Suspense } from 'react'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'

// The body pulls in the Markdown renderer, so it loads on first open only.
const HelpDrawerBody = lazy(() => import('./HelpDrawerBody'))

interface HelpDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The article for the current page, from the help context map. */
  slug: string
}

/** Right-side help panel opened with the `?` key. */
export function HelpDrawer({ open, onOpenChange, slug }: HelpDrawerProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
        <D.Content
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => {
            // Escape in a filled search box clears it (handled by the input) instead of closing.
            const t = e.target
            if (t instanceof HTMLInputElement && 'helpSearch' in t.dataset && t.value)
              e.preventDefault()
          }}
          className="fixed inset-y-0 right-0 z-50 flex w-[580px] max-w-[calc(100vw-2rem)] animate-sheet-in flex-col border-l border-line bg-surface shadow-pop outline-none"
        >
          <D.Title className="sr-only">Help</D.Title>
          <Suspense fallback={<DrawerSkeleton />}>
            <HelpDrawerBody initialSlug={slug} onClose={() => onOpenChange(false)} />
          </Suspense>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

function DrawerSkeleton() {
  return (
    <div className="p-5" aria-busy>
      <Skeleton className="h-8 w-full" />
      <Skeleton className="mt-8 h-6 w-2/3" />
      <SkeletonText lines={6} className="mt-5" />
    </div>
  )
}
